import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, ArrowLeft, Mail, KeyRound, CheckCircle } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Password reset state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: enter email, 2: enter new password, 3: success
  const [resetEmail, setResetEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const { login, register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/";

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  /*
   * The page itself is black, but <html>/<body> still carry the warm paper
   * canvas, which shows through on overscroll and behind the route.
   *
   * The class covers the frame before React mounts on a direct /login load.
   * The inline background is also needed: arriving at "/" boots the pre-paint
   * script down its non-login branch, which sets an inline canvas colour on
   * <html>, and an inline style beats the class rule. Take the inline value
   * over while mounted and hand it back on unmount, so the rest of the app
   * keeps its canvas.
   */
  useEffect(() => {
    const root = document.documentElement;
    const previousBackground = root.style.backgroundColor;
    root.classList.add("login-page");
    root.style.backgroundColor = "#000000";
    return () => {
      root.classList.remove("login-page");
      root.style.backgroundColor = previousBackground;
    };
  }, []);

  // Show loading state while checking auth
  if (isAuthenticated) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success(t("auth.welcomeBack"));
      } else {
        if (!name.trim()) {
          toast.error(t("common.required") + ": " + t("common.name"));
          setLoading(false);
          return;
        }
        await register(email, password, name);
        toast.success(t("common.success"));
      }
      navigate(from, { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || t("auth.invalidCredentials");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Password reset handlers
  const handleResetRequest = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    
    setResetLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/request-password-reset`, {
        email: resetEmail.trim()
      });
      
      // Store the token (in real app this would be sent via email)
      setResetToken(response.data.reset_token);
      setResetStep(2);
      toast.success("Enter your new password below.");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to request password reset. Please check your email address.";
      toast.error(message);
    } finally {
      setResetLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setResetLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      
      setResetStep(3);
      toast.success("Password reset successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to reset password. Please try again.";
      toast.error(message);
    } finally {
      setResetLoading(false);
    }
  };

  const closeResetDialog = () => {
    setShowResetDialog(false);
    setResetStep(1);
    setResetEmail("");
    setResetToken("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const openResetDialog = () => {
    setResetEmail(email); // Pre-fill with login email if entered
    setShowResetDialog(true);
  };

  return (
    /*
     * The login page is a dark section end to end — black with white text, in
     * both themes. Colours here are explicit rather than tokenised on purpose:
     * the page must not follow light mode, so it never reads `bg-background`.
     */
    <div className="min-h-screen bg-ink text-white flex selection:bg-white/20 selection:text-white">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden lg:border-r lg:border-white/10">
        <div className="relative z-10">
          <img 
            src="/trp-logo.png" 
            alt="The Render Pros" 
            className="h-12 object-contain"
          />
        </div>

        <div className="space-y-8 relative z-10">
          <div>
            <h2 className="text-4xl font-light text-white tracking-tight leading-tight" style={{ fontFamily: 'var(--font-sans)' }}>
              Control execution.<br />
              Surface risk early.<br />
              Enforce predictability.
            </h2>
          </div>
          <p className="text-white/60 text-base max-w-md leading-relaxed">
            An internal operating system designed for fast-scaling creative companies. 
            Know what's at risk, what needs attention, and where execution is blocked—at any moment.
          </p>
        </div>

        <p className="text-white/40 text-xs relative z-10">
          Built with discipline. No feature bloat.
        </p>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Language Switcher */}
          <div className="absolute top-4 right-4 [&_button]:hover:bg-white/10 [&_span]:text-white/70 [&_svg]:text-white/70">
            <LanguageSwitcher variant="dropdown" />
          </div>

          {/* Mobile Logo */}
          <div className="lg:hidden mb-12 text-center">
            <img 
              src="/trp-logo.png" 
              alt="The Render Pros" 
              className="h-10 object-contain mx-auto mb-2"
            />
            <p className="text-[10px] font-medium uppercase tracking-wider text-white/40">Operations OS</p>
          </div>

          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-light tracking-tight text-white" style={{ fontFamily: 'var(--font-sans)' }}>
                {isLogin ? t("auth.welcomeBack") : t("common.create") + " " + t("settings.profile.title").toLowerCase()}
              </h2>
              <p className="text-white/55 text-sm mt-1">
                {isLogin 
                  ? t("auth.loginSubtitle")
                  : "Set up your COO account to get started"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white/70">{t("common.name")}</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Smith"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required={!isLogin}
                    data-testid="input-name"
                    className="h-11 bg-white/[0.04] border-white/15 text-white placeholder:text-white/35 focus-visible:border-white/40 focus-visible:ring-white/20"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/70">{t("auth.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@renderpros.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="input-email"
                  className="h-11 bg-white/[0.04] border-white/15 text-white placeholder:text-white/35 focus-visible:border-white/40 focus-visible:ring-white/20"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-white/70">{t("auth.password")}</Label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={openResetDialog}
                      className="text-xs text-white/55 hover:text-white transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="input-password"
                    className="h-11 bg-white/[0.04] border-white/15 text-white placeholder:text-white/35 focus-visible:border-white/40 focus-visible:ring-white/20 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/55 hover:text-white transition-colors"
                    data-testid="toggle-password"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-full font-medium bg-[#FFFFFF] text-[#000000] hover:bg-[#FFFFFF]"
                data-testid="submit-btn"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    {isLogin ? t("auth.signIn") : t("common.create")}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-white/55 hover:text-white transition-colors"
                data-testid="toggle-auth-mode"
              >
                {isLogin 
                  ? "Don't have an account? Create one" 
                  : "Already have an account? Sign in"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {resetStep === 3 ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <KeyRound className="w-5 h-5" />
              )}
              {resetStep === 1 && "Reset Password"}
              {resetStep === 2 && "Set New Password"}
              {resetStep === 3 && "Password Reset Complete"}
            </DialogTitle>
            <DialogDescription>
              {resetStep === 1 && "Enter your email address and we'll help you reset your password."}
              {resetStep === 2 && "Enter your new password below."}
              {resetStep === 3 && "Your password has been successfully reset. You can now sign in with your new password."}
            </DialogDescription>
          </DialogHeader>

          {resetStep === 1 && (
            <form onSubmit={handleResetRequest} className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="john@renderpros.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    className="pl-10"
                    data-testid="reset-email-input"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeResetDialog}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={resetLoading}
                  className="flex-1"
                  data-testid="reset-request-btn"
                >
                  {resetLoading ? (
                    <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Continue"
                  )}
                </Button>
              </div>
            </form>
          )}

          {resetStep === 2 && (
            <form onSubmit={handlePasswordReset} className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setResetStep(1)}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={resetLoading}
                  className="flex-1"
                  data-testid="reset-password-btn"
                >
                  {resetLoading ? (
                    <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </div>
            </form>
          )}

          {resetStep === 3 && (
            <div className="pt-4">
              <Button
                onClick={closeResetDialog}
                className="w-full"
                data-testid="reset-done-btn"
              >
                Back to Sign In
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
